import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import Auth from '../../../api/auth';
import Table from '../../../components/Table';
import useApiQuery from '../../../hooks/useApiQuery';
import rbacCheck from '../../../rbac/rbacCheck';
import OrganizationBreadCrumb from '../OrganizationBreadCrumb';

import CreateBusinessUnitFormModal from './CreateBusinessUnitFormModal';

import type { BusinessUnit } from '@/types/businessUnitTypes';
import type { RoleKey } from '@/types/rules';
import type { TableError, TableRowData } from '@/types/tableTypes';

interface RootState {
  user: {
    roles: RoleKey[];
  };
}

const BusinessUnits: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const intl = useIntl();
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.user);

  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  const { makeApiCall: getBusinessUnitsCall } = useApiQuery(
    Auth.fetchV1BusinessUnits as unknown as (...args: unknown[]) => Promise<unknown>
  );

  const canCreateBusinessUnit = rbacCheck(userData.roles, '/business-units:POST');

  useEffect(() => {
    const getBusinessUnits = async (): Promise<void> => {
      const data = await getBusinessUnitsCall({ organizationId });
      if (data) {
        setBusinessUnits(data as BusinessUnit[]);
        setFetchError(null);
      }
    };

    getBusinessUnits().catch(setFetchError);
  }, [getBusinessUnitsCall, organizationId]);

  const fields = [
    {
      id: 'name',
      title: 'Name',
    },
    {
      id: 'description',
      title: 'Description',
    },
    {
      id: 'businessUnitManager',
      title: 'Business Unit Manager',
    },
  ];

  const rows = businessUnits.map(bu => ({
    key: bu._id,
    onRowClick: () => navigate(`/business-units/${bu._id}`),
    data: [
      {
        field: 'name',
        value: bu.name,
      },
      {
        field: 'description',
        value: bu.description,
      },
      {
        field: 'businessUnitManager',
        value: bu.businessUnitManager?.email,
      },
    ],
  }));

  return (
    <>
      {createModalOpened && (
        <CreateBusinessUnitFormModal
          onSubmit={(newBusinessUnit: BusinessUnit) => {
            setBusinessUnits(prev => [...prev, newBusinessUnit]);
          }}
          onCancel={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      <OrganizationBreadCrumb />
      <Table
        header='Business Units'
        fields={fields}
        rows={rows as TableRowData[]}
        error={fetchError as TableError | string | null}
        createAction={{
          disabled: !canCreateBusinessUnit,
          onClick: () => {
            setCreateModalOpened(true);
          },
          tooltip: !canCreateBusinessUnit
            ? intl.formatMessage({ id: 'businessUnitCreatePermissionErr' })
            : '',
        }}
      />
    </>
  );
};

export default BusinessUnits;
